"use client";

import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useState } from "react";
export default function Roadmap() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: timelineRef,
    isVisible: timelineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: progressRef,
    isVisible: progressVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const [progressValue, setProgressValue] = useState(0);
  useEffect(() => {
    if (progressVisible) {
      const timer = setTimeout(() => setProgressValue(70), 200);
      return () => clearTimeout(timer);
    }
  }, [progressVisible]);
  const steps = [{
    number: "01",
    title: "Initial Inquiry",
    description: "Reach out to our team to discuss your family's specific needs and court-ordered requirements.",
    duration: "15 minutes"
  }, {
    number: "02",
    title: "Intake Review",
    description: "We carefully review court documentation and safety protocols to ensure a neutral, secure environment.",
    duration: "2 days"
  }, {
    number: "03",
    title: "Service Setup",
    description: "Finalizing schedules, logistics, and communication plans tailored to your family's unique situation.",
    duration: "3 days"
  }, {
    number: "04",
    title: "First Session",
    description: "The initial supervised parenting time or exchange occurs in a safe, structured, and child-centered setting.",
    duration: "Scheduled"
  }, {
    number: "05",
    title: "Ongoing Support",
    description: "Consistent, professional monitoring with detailed observation reporting provided to the court.",
    duration: "Ongoing"
  }];
  return <section data-section-id="270" id="the-path-forward" className="relative overflow-hidden py-32 bg-[#2F3631] text-[#E7D8C7]">
      <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
        {}
        <div ref={headerRef} className={`max-w-3xl mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-xs font-mono uppercase tracking-[0.2em] mb-6" style={{
          color: "#8FA27C"
        }}>
            § 01 — The Intake Path
          </p>
          <h2 className="font-default text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6 text-[#E7D8C7]">
            A clear, structured{" "}
            <em className="italic" style={{
            color: "#8FA27C"
          }}>
              path to stability.
            </em>
          </h2>
          <p className="font-default text-base lg:text-lg text-[#E7D8C7]/70 leading-relaxed max-w-2xl">
            We provide a calm, neutral, and trauma-informed process for families navigating court-ordered contact. 
            Our intake is designed to be simple, transparent, and focused on the well-being of the children involved.
          </p>
        </div>

        {}
        <div ref={timelineRef} className="relative">
          {}
          <div className="hidden lg:block absolute top-[50%] left-0 right-0 h-px" style={{
          backgroundColor: "#8FA27C"
        }} />

          {}
          <div className="lg:hidden absolute top-0 bottom-0 left-6 w-px" style={{
          backgroundColor: "#8FA27C"
        }} />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-4 relative">
            {steps.map((step, i) => <div key={i} data-index={i} className={`relative transition-all duration-700 ease-out ${timelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                {}
                <div className="hidden lg:flex absolute top-[calc(50%-6px)] left-1/2 -translate-x-1/2 z-10">
                  <span className="block w-3 h-3 rounded-lg" style={{
                backgroundColor: "#8FA27C",
                boxShadow: "0 0 0 4px #2F3631, 0 0 20px #8FA27C / 0.4"
              }} />
                </div>

                {}
                <div className="lg:hidden absolute top-6 left-6 -translate-x-1/2 z-10">
                  <span className="block w-3 h-3 rounded-lg" style={{
                backgroundColor: "#8FA27C",
                boxShadow: "0 0 0 4px #2F3631, 0 0 20px #8FA27C / 0.4"
              }} />
                </div>

                <Card className="lg:mb-12 ml-14 lg:ml-0 p-6 rounded-lg border bg-[#2F3631] hover:bg-[#3a423c] transition-colors duration-300 gap-0" style={{
              borderColor: "#8FA27C",
              boxShadow: "var(--shadow-lg)"
            }}>
                  <div className="font-default text-5xl mb-4 leading-none" style={{
                color: "#8FA27C"
              }}>
                    {step.number}
                  </div>
                  <h3 className="font-default text-xl mb-3 text-[#E7D8C7] leading-snug">
                    {step.title}
                  </h3>
                  <p className="font-default text-sm text-[#E7D8C7]/65 leading-relaxed mb-5 line-clamp-2">
                    {step.description}
                  </p>
                  <div className="pt-4 border-t" style={{
                borderColor: "#8FA27C"
              }}>
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{
                  color: "#8FA27C"
                }}>
                      Timeframe: {step.duration}
                    </span>
                  </div>
                </Card>
              </div>)}
          </div>
        </div>

        {}
        <div ref={progressRef} className={`mt-24 max-w-2xl mx-auto transition-all duration-1000 ease-out ${progressVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-between mb-4"><span className="font-mono text-[11px] uppercase tracking-[0.2em]" data-ast-id="f:/src/pages/Home/sections/Roadmap.tsx|sig:jxvpyy" data-ast-start="6462" data-ast-end="6654" style={{
            color: 'rgb(143, 162, 124)'
          }}>AVERAGE INTAKE SPEED (*DEPENDENT ON PARENTS COOPERATION)</span><span className="font-default text-2xl text-[#E7D8C7]" data-ast-id="f:/src/pages/Home/sections/Roadmap.tsx|sig:l52p2y" data-ast-start="6667" data-ast-end="6735">5 days</span></div>
          <Progress value={progressValue} className="h-1 bg-[#E7D8C7]/10 [&>[data-slot=progress-indicator]]:bg-[#8FA27C] transition-all duration-[1500ms] ease-out" />
          <p className="font-default text-xs text-[#E7D8C7]/50 mt-4 text-center">
            We prioritize efficiency to ensure families can begin their scheduled services as quickly as possible.
          </p>
        </div>
      </div>
    </section>;
}